import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import InvoicePreview from './InvoicePreview';

import { saveMonthYear, loadMonthYear } from '../utils/monthYearPersistence';
import '../assets/FinanceICInvoiceManagement.css';
import '../assets/Pagination.css';

const FinanceICInvoiceManagement = ({ user }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Month/Year selection at top with persistence
  const savedMonthYear = loadMonthYear('finance_ic_invoices');
  const [selectedMonth, setSelectedMonth] = useState(savedMonthYear.month);
  const [selectedYear, setSelectedYear] = useState(savedMonthYear.year);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: ''
  });

  // Modal states
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Persistent result popup for the bulk WhatsApp send (sent/skipped/failed + per-tutee details)
  const [whatsappResult, setWhatsappResult] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const itemsPerPage = 20;

  // Sorting
  const [sortField, setSortField] = useState('invoiceNumber');
  const [sortDirection, setSortDirection] = useState('asc');

  // Determine if user is dual role (Finance IC + Profit Sharer)
  const isDualRole = user?.isCoFounder && user?.financeICSettings?.isEligible;
  const [viewMode, setViewMode] = useState('assigned'); // 'assigned' or 'all'

  useEffect(() => {
    fetchInvoices();
  }, [filters, currentPage, viewMode, selectedMonth, selectedYear, sortField, sortDirection]);

  const fetchInvoices = async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        month: selectedMonth,
        year: selectedYear,
        page: currentPage,
        limit: itemsPerPage,
        viewMode: viewMode, // Pass viewMode to backend
        sortBy: sortField,
        sortOrder: sortDirection,
        ...filters
      };

      const response = await adminApi.getTuteeInvoices(params);
      
      if (response.data.success) {
        const invoiceData = response.data.data || response.data;
        setInvoices(invoiceData.invoices || []);
        setTotalPages(invoiceData.pagination?.pages || 1);
        setTotalInvoices(invoiceData.pagination?.total || 0);
      } else {
        setError('Failed to load invoices');
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setError(error.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Manual workflow (Copy Message, Mark as Sent, Submit Dispute, Mark as Paid)
  // was removed — sending is via WhatsApp and payment is verified by the
  // Payment IC over WhatsApp. Finance IC now only sends + monitors.
  const [whatsappSending, setWhatsappSending] = useState(false);

  const handleSendWhatsApp = async (invoice) => {
    if (!window.confirm(`Send the ${new Date(2000, invoice.month - 1).toLocaleString('en-US', { month: 'long' })} invoice to ${invoice.tutee?.parent?.name || invoice.parent?.name || 'the parent'} via WhatsApp?`)) {
      return;
    }

    setWhatsappSending(true);
    setError('');
    try {
      const response = await adminApi.sendTuteeInvoiceWhatsApp(invoice._id);
      if (response.data.success) {
        // Stays until the user dismisses it (no auto-timeout) so the send
        // confirmation isn't missed.
        setSuccessMessage(response.data.message || 'Invoice sent via WhatsApp.');
        fetchInvoices();
      } else {
        setError(response.data.message || 'Failed to send via WhatsApp');
      }
    } catch (err) {
      console.error('Error sending invoice via WhatsApp:', err);
      setError(err.response?.data?.message || 'Failed to send invoice via WhatsApp');
    } finally {
      setWhatsappSending(false);
    }
  };

  const handleSendAllWhatsApp = async () => {
    const sendable = invoices.filter(inv => ['finalized', 'sent'].includes(inv.status));
    if (sendable.length === 0) {
      setError('No finalized invoices to send via WhatsApp.');
      return;
    }

    const monthName = new Date(2000, selectedMonth - 1).toLocaleString('en-US', { month: 'long' });
    if (!window.confirm(`Send all invoices for ${monthName} ${selectedYear} to parents via WhatsApp? Parents with multiple children get one combined message.`)) {
      return;
    }

    setWhatsappSending(true);
    setError('');
    try {
      const response = await adminApi.sendBulkTuteeInvoiceWhatsApp(selectedMonth, selectedYear);
      if (response.data.success) {
        const d = response.data.data || {};
        // Show a persistent result popup (per-tutee status) instead of a toast
        // that disappears before the user can read it.
        setWhatsappResult({
          month: selectedMonth,
          year: selectedYear,
          total: d.total || 0,
          sent: d.sent || 0,
          skipped: d.skipped || 0,
          failed: d.failed || 0,
          details: d.details || [],
          message: response.data.message || ''
        });
        fetchInvoices();
      } else {
        setError(response.data.message || 'Failed to send invoices via WhatsApp');
      }
    } catch (err) {
      console.error('Error in bulk WhatsApp send:', err);
      setError(err.response?.data?.message || 'Failed to send invoices via WhatsApp');
    } finally {
      setWhatsappSending(false);
    }
  };

  const handleSendReminders = async () => {
    const remindable = invoices.filter(
      inv => inv.status === 'sent' && inv.paymentStatus === 'unpaid' && !inv.paymentIndicated
    );
    if (remindable.length === 0) {
      setError('No unpaid invoices to remind for this period.');
      return;
    }

    const monthName = new Date(2000, selectedMonth - 1).toLocaleString('en-US', { month: 'long' });
    if (!window.confirm(`Send a payment reminder to all parents with unpaid invoices for ${monthName} ${selectedYear}? Parents who already tapped "I've paid" are skipped.`)) {
      return;
    }

    setWhatsappSending(true);
    setError('');
    try {
      const response = await adminApi.sendBulkPaymentReminders(selectedMonth, selectedYear);
      if (response.data.success) {
        const d = response.data.data || {};
        setWhatsappResult({
          month: selectedMonth,
          year: selectedYear,
          total: d.total || 0,
          sent: d.sent || 0,
          skipped: d.skipped || 0,
          failed: d.failed || 0,
          details: d.details || [],
          message: response.data.message || ''
        });
        fetchInvoices();
      } else {
        setError(response.data.message || 'Failed to send reminders');
      }
    } catch (err) {
      console.error('Error in bulk reminder send:', err);
      setError(err.response?.data?.message || 'Failed to send reminders');
    } finally {
      setWhatsappSending(false);
    }
  };

  const handleDownloadPDF = async (invoiceId, invoice) => {
    try {
      const response = await adminApi.downloadTuteeInvoicePDF(invoiceId);
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or generate from invoice data
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'invoice.pdf';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else if (invoice) {
        // Fallback: generate filename from invoice data
        const sanitizedTuteeName = invoice.tutee?.name.toLowerCase().replace(/[^a-z0-9]/g, '_') || 'tutee';
        const tuteeCode = invoice.tutee?.code ? invoice.tutee.code.toUpperCase().replace(/[^A-Z0-9\-]/g, '_') + '_' : '';
        const monthPadded = (invoice.month || selectedMonth).toString().padStart(2, '0');
        const year = invoice.year || selectedYear;
        filename = `invoice_tutee_${tuteeCode}${sanitizedTuteeName}_${monthPadded}_${year}.pdf`;
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download invoice PDF');
    }
  };

  const handleDownloadAllInvoices = async () => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthName = monthNames[selectedMonth - 1];
    
    if (!window.confirm(`Download all ${totalInvoices} assigned invoices for ${monthName} ${selectedYear} as ZIP?`)) {
      return;
    }

    try {
      setLoading(true);
      const params = {
        month: selectedMonth,
        year: selectedYear
      };

      const response = await adminApi.downloadAllAssignedInvoices(params);
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'my-assigned-invoices.zip';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccessMessage('All invoices downloaded successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error downloading all invoices:', error);
      setError(error.response?.data?.message || 'Failed to download invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setLoadingPreview(true);
    setShowInvoicePreview(true);
    
    try {
      // Fetch full invoice details
      const response = await adminApi.getTuteeInvoiceById(invoice._id);
      
      if (response.data.success) {
        setSelectedInvoice(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      setError('Failed to load invoice details');
    } finally {
      setLoadingPreview(false);
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount).toFixed(2);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-SG', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-badge status-pending',
      assigned: 'status-badge status-assigned',
      paid: 'status-badge status-paid',
      disputed: 'status-badge status-disputed',
      resolved: 'status-badge status-resolved'
    };
    return <span className={statusClasses[status] || 'status-badge'}>{status}</span>;
  };

  const getPaymentStatusBadge = (invoice) => {
    if (invoice.paymentStatus === 'paid' && invoice.paymentDate) {
      const paidDate = new Date(invoice.paymentDate).toLocaleDateString('en-SG', {
        timeZone: 'Asia/Singapore',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
      return (
        <div className="payment-status-paid-info">
          <span className="payment-status-badge payment-paid">Paid on {paidDate}</span>
        </div>
      );
    }
    
    if (invoice.paymentStatus === 'unpaid' && invoice.status === 'finalized') {
      return <span className="payment-status-badge payment-not-sent">Not Sent</span>;
    }

    // Sent to the parent but payment not yet received/verified.
    if (invoice.paymentStatus === 'unpaid' && invoice.status === 'sent') {
      return <span className="payment-status-badge payment-sent">Sent</span>;
    }

    const statusClasses = {
      unpaid: 'payment-status-badge payment-unpaid',
      paid: 'payment-status-badge payment-paid',
      partial: 'payment-status-badge payment-partial'
    };
    return <span className={statusClasses[invoice.paymentStatus] || 'payment-status-badge'}>{invoice.paymentStatus}</span>;
  };

  const isOverdue = (invoice) => {
    if (invoice.paymentStatus === 'paid') return false;
    return new Date(invoice.paymentDueDate) < new Date();
  };

  const canEdit = viewMode === 'assigned';

  // Breakdown of how much to collect from each Payment IC across the loaded
  // invoices (the Finance IC monitors these but a Payment IC actually collects).
  const paymentICBreakdown = (() => {
    const map = {};
    invoices.forEach(inv => {
      const name = inv.paymentIC?.paynowName || inv.paymentInstructions?.paynowName;
      if (!name) return;
      const key = inv.paymentIC?._id || name;
      if (!map[key]) {
        map[key] = {
          name,
          number: inv.paymentIC?.paynowNumber || inv.paymentInstructions?.paynowNumber || '',
          count: 0,
          total: 0,
          collected: 0
        };
      }
      map[key].count++;
      map[key].total += inv.finalAmount || 0;
      if (inv.paymentStatus === 'paid') map[key].collected += inv.finalAmount || 0;
    });
    return Object.values(map);
  })();

  return (
    <div className="finance-ic-invoice-management">
      <div className="page-header">
        <h2>
          {isDualRole && viewMode === 'all' ? 'All Tutee Invoices (Profit Sharer View)' : 'My Assigned Invoices'}
        </h2>
        <p className="subtitle">
          {isDualRole && viewMode === 'all' 
            ? 'View all tutee invoices (read-only access)'
            : 'Manage invoices assigned to you as Finance IC'}
        </p>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {successMessage && (
        <div className="success-message">
          <span>{successMessage}</span>
          <button
            type="button"
            className="success-message-close"
            onClick={() => setSuccessMessage('')}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Dual Role View Switcher */}
      {isDualRole && (
        <div className="view-mode-switcher">
          <button
            className={`view-mode-btn ${viewMode === 'assigned' ? 'active' : ''}`}
            onClick={() => setViewMode('assigned')}
          >
            My Assigned Invoices
          </button>
          <button
            className={`view-mode-btn ${viewMode === 'all' ? 'active' : ''}`}
            onClick={() => setViewMode('all')}
          >
            All Invoices (Profit Sharer)
          </button>
        </div>
      )}

      {/* Payment IC collection breakdown — how much each Payment IC collects */}
      {paymentICBreakdown.length > 0 && (
        <div className="fic-paymentic-breakdown">
          <h3>Collection by Payment IC</h3>
          <table className="fic-breakdown-table">
            <thead>
              <tr>
                <th>Payment IC</th>
                <th>Invoices</th>
                <th>To Collect</th>
                <th>Collected</th>
                <th>Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {paymentICBreakdown.map((ic, i) => (
                <tr key={i}>
                  <td className="fic-bd-name">
                    {ic.name}
                    {ic.number && <span className="fic-bd-sub"> {ic.number}</span>}
                  </td>
                  <td>{ic.count}</td>
                  <td>${ic.total.toFixed(2)}</td>
                  <td className="fic-bd-paid">${ic.collected.toFixed(2)}</td>
                  <td className="fic-bd-outstanding">${(ic.total - ic.collected).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Combined Month/Year + Status filters in one section */}
      <div className="filters-section">
        <div className="selector-group">
          <label>Month:</label>
          <select
            value={selectedMonth}
            onChange={(e) => {
              const newMonth = parseInt(e.target.value);
              setSelectedMonth(newMonth);
              saveMonthYear('finance_ic_invoices', newMonth, selectedYear);
              setCurrentPage(1);
            }}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
              <option key={m} value={m}>
                {new Date(2000, m - 1).toLocaleString('en-US', { month: 'long' })}
              </option>
            ))}
          </select>
        </div>

        <div className="selector-group">
          <label>Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => {
              const newYear = parseInt(e.target.value);
              setSelectedYear(newYear);
              saveMonthYear('finance_ic_invoices', selectedMonth, newYear);
              setCurrentPage(1);
            }}
          >
            {[...Array(5)].map((_, i) => {
              const year = new Date().getFullYear() - i;
              return <option key={year} value={year}>{year}</option>;
            })}
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            <option value="finalized">Finalized</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Payment Status:</label>
          <select
            value={filters.paymentStatus}
            onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })}
          >
            <option value="">All</option>
            <option value="unpaid">Unpaid</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
          </select>
        </div>

        <button className="btn-clear-filters" onClick={() => setFilters({ status: '', paymentStatus: '' })}>
          Clear Filters
        </button>
      </div>

      {/* Summary Stats */}
      <div className="summary-stats">
        <div className="stat-card">
          <div className="stat-label">Total Families</div>
          <div className="stat-value">
            {new Set(invoices.map(inv => inv.tutee?.familyNumber).filter(Boolean)).size}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Invoices</div>
          <div className="stat-value">{totalInvoices}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Unpaid</div>
          <div className="stat-value">
            {invoices.filter(inv => inv.paymentStatus === 'unpaid').length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Paid</div>
          <div className="stat-value">
            {invoices.filter(inv => inv.paymentStatus === 'paid').length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Overdue</div>
          <div className="stat-value warning">
            {invoices.filter(inv => isOverdue(inv)).length}
          </div>
        </div>
      </div>

      {/* Download All Button */}
      {canEdit && totalInvoices > 0 && (
        <div className="download-all-section">
          <button className="btn-download-all-subtle" onClick={handleDownloadAllInvoices}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
            </svg>
            Download All as ZIP ({totalInvoices} invoices)
          </button>
          {invoices.some(inv => ['finalized', 'sent'].includes(inv.status)) && (
            <button
              className="btn-send-all-whatsapp"
              onClick={handleSendAllWhatsApp}
              disabled={whatsappSending}
              title="Send all invoices to parents via WhatsApp (one message per family)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm0 18.15h-.01c-1.52 0-3.01-.41-4.3-1.18l-.31-.18-3.19.84.85-3.11-.2-.32a8.2 8.2 0 01-1.26-4.36c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 012.41 5.83c0 4.54-3.69 8.23-8.24 8.23z"/>
              </svg>
              {whatsappSending ? 'Sending...' : `Send All via WhatsApp (${invoices.filter(inv => ['finalized', 'sent'].includes(inv.status)).length})`}
            </button>
          )}
          {invoices.some(inv => inv.status === 'sent' && inv.paymentStatus === 'unpaid' && !inv.paymentIndicated) && (
            <button
              className="btn-send-all-whatsapp btn-send-reminders"
              onClick={handleSendReminders}
              disabled={whatsappSending}
              title="Send a payment reminder to parents with unpaid invoices"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2a6 6 0 0 0-6 6v3.6l-1.7 3.4A1 1 0 0 0 5.2 16h13.6a1 1 0 0 0 .9-1l-1.7-3.4V8a6 6 0 0 0-6-6zm0 20a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22z"/>
              </svg>
              {whatsappSending
                ? 'Sending...'
                : `Remind Unpaid (${invoices.filter(inv => inv.status === 'sent' && inv.paymentStatus === 'unpaid' && !inv.paymentIndicated).length})`}
            </button>
          )}
        </div>
      )}

      {/* Invoices Table */}
      {loading ? (
        <LoadingSpinner />
      ) : invoices.length === 0 ? (
        <div className="no-data">
          <p>No invoices found</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="invoices-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('invoiceNumber')}>
                    Invoice #
                    {sortField === 'invoiceNumber' && (
                      <span className="sort-indicator-v2">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('parent')}>
                    Parent
                    {sortField === 'parent' && (
                      <span className="sort-indicator-v2">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('tutee')}>
                    Tutee
                    {sortField === 'tutee' && (
                      <span className="sort-indicator-v2">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th>Payment IC</th>
                  <th className="sortable" onClick={() => handleSort('finalAmount')}>
                    Amount
                    {sortField === 'finalAmount' && (
                      <span className="sort-indicator-v2">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('paymentStatus')}>
                    Payment
                    {sortField === 'paymentStatus' && (
                      <span className="sort-indicator-v2">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('paymentDueDate')}>
                    Due Date
                    {sortField === 'paymentDueDate' && (
                      <span className="sort-indicator-v2">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(invoice => (
                  <tr key={invoice._id} className={isOverdue(invoice) ? 'overdue-row' : ''}>
                    <td className="invoice-number-cell">{invoice.invoiceNumber}</td>
                    <td>
                      <div className="parent-info">
                        <div>{invoice.tutee?.parent?.name || invoice.parent?.name}</div>
                        <div className="parent-phone">{invoice.tutee?.parent?.phone || invoice.parent?.phone || 'N/A'}</div>
                      </div>
                    </td>
                    <td>{invoice.tutee?.name || 'Unknown'}</td>
                    <td>
                      {(invoice.paymentIC?.paynowName || invoice.paymentInstructions?.paynowName) ? (
                        <div className="parent-info">
                          <div>{invoice.paymentIC?.paynowName || invoice.paymentInstructions?.paynowName}</div>
                          <div className="parent-phone">
                            {invoice.paymentIC?.paynowNumber || invoice.paymentInstructions?.paynowNumber || ''}
                          </div>
                        </div>
                      ) : (
                        <span className="parent-phone">—</span>
                      )}
                    </td>
                    <td className="amount">${formatCurrency(invoice.finalAmount)}</td>
                    <td>{getPaymentStatusBadge(invoice)}</td>
                    <td className={isOverdue(invoice) ? 'overdue-date' : ''}>
                      {formatDate(invoice.paymentDueDate)}
                      {isOverdue(invoice) && <span className="overdue-label">OVERDUE</span>}
                    </td>
                    <td className="actions-cell">
                      <div className="action-buttons-v2">
                        {/* View button */}
                        <button
                          className="btn-icon-v2"
                          onClick={() => handleViewInvoice(invoice)}
                          title="View"
                        >
                          <svg viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                          </svg>
                        </button>

                        {/* Download button */}
                        <button
                          className="btn-icon-v2"
                          onClick={() => handleDownloadPDF(invoice._id, invoice)}
                          title="Download PDF"
                        >
                          <svg viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>

                        {/* Send via WhatsApp — Finance IC, for finalized or already-sent (resend) */}
                        {canEdit && ['finalized', 'sent'].includes(invoice.status) && (
                          <button
                            className="btn-icon-v2 btn-whatsapp"
                            onClick={() => handleSendWhatsApp(invoice)}
                            disabled={whatsappSending}
                            title={invoice.status === 'sent' ? 'Resend to parent via WhatsApp' : 'Send to parent via WhatsApp'}
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm0 18.15h-.01c-1.52 0-3.01-.41-4.3-1.18l-.31-.18-3.19.84.85-3.11-.2-.32a8.2 8.2 0 01-1.26-4.36c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 012.41 5.83c0 4.54-3.69 8.23-8.24 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.42-.14-.01-.31-.01-.48-.01-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29z"/>
                            </svg>
                          </button>
                        )}

                        {/* Read-only issue indicator — parent tapped "Invoice has issues" */}
                        {invoice.issueReported && (
                          <span
                            className="issue-flag"
                            title={`Parent flagged an issue${invoice.issueReportedAt ? ' on ' + new Date(invoice.issueReportedAt).toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore', day: '2-digit', month: 'short' }) : ''}`}
                            aria-label="Parent reported an issue with this invoice"
                          >
                            <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn-page"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn-page"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Invoice Preview Modal */}
      {showInvoicePreview && selectedInvoice && (
        <div className="modal-overlay" onClick={() => {
          setShowInvoicePreview(false);
          setSelectedInvoice(null);
        }}>
          <div className="modal-content-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Invoice Preview - {selectedInvoice.invoiceNumber}</h2>
              <button 
                className="modal-close-btn"
                onClick={() => {
                  setShowInvoicePreview(false);
                  setSelectedInvoice(null);
                }}
                title="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {loadingPreview ? (
                <LoadingSpinner />
              ) : selectedInvoice ? (
                <InvoicePreview
                  invoiceType="tutee"
                  recipient={{
                    _id: selectedInvoice.tutee?._id,
                    name: selectedInvoice.tutee?.name,
                    email: selectedInvoice.tutee?.parent?.email || selectedInvoice.parent?.email,
                    phone: selectedInvoice.tutee?.parent?.phone || selectedInvoice.parent?.phone
                  }}
                  month={selectedInvoice.month}
                  year={selectedInvoice.year}
                  invoiceData={{
                    lessons: selectedInvoice.lessons || [],
                    adjustments: selectedInvoice.adjustments || [],
                    discounts: selectedInvoice.discounts || [],
                    recurringPayments: [],
                    totals: {
                      lessonEarnings: selectedInvoice.subtotal || 0,
                      adjustmentTotal: (selectedInvoice.adjustments || []).reduce((sum, adj) => sum + adj.amount, 0),
                      discountTotal: (selectedInvoice.discounts || []).reduce((sum, disc) => sum + disc.amount, 0),
                      recurringTotal: 0,
                      finalAmount: selectedInvoice.finalAmount || 0
                    },
                    lessonEarnings: selectedInvoice.subtotal || 0,
                    subtotal: selectedInvoice.subtotal || 0,
                    invoiceId: selectedInvoice._id,
                    invoiceNumber: selectedInvoice.invoiceNumber,
                    paymentDueDate: selectedInvoice.paymentDueDate,
                    paymentInstructions: selectedInvoice.paymentInstructions
                      || (selectedInvoice.paymentIC ? {
                        paynowNumber: selectedInvoice.paymentIC.paynowNumber,
                        paynowName: selectedInvoice.paymentIC.paynowName,
                        additionalInstructions: selectedInvoice.paymentIC.additionalInstructions
                      } : null)
                  }}
                  onClose={() => {
                    setShowInvoicePreview(false);
                    setSelectedInvoice(null);
                  }}
                />
              ) : (
                <p>Failed to load invoice details</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp bulk-send result popup (persists until dismissed) */}
      {whatsappResult && (
        <div className="modal-overlay" onClick={() => setWhatsappResult(null)}>
          <div
            className="wa-result-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 10, maxWidth: 560, width: '92%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}
          >
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#893C3B' }}>
                WhatsApp Send Results — {new Date(2000, whatsappResult.month - 1).toLocaleString('en-US', { month: 'long' })} {whatsappResult.year}
              </h3>
              <button
                onClick={() => setWhatsappResult(null)}
                aria-label="Close"
                style={{ background: 'none', border: 'none', fontSize: 26, lineHeight: 1, color: '#888', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '16px 22px', borderBottom: '1px solid #f3f3f3', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600 }}>Total: {whatsappResult.total}</span>
              <span style={{ color: '#10b981', fontWeight: 700 }}>✓ Sent: {whatsappResult.sent}</span>
              <span style={{ color: '#f59e0b', fontWeight: 700 }}>⚠ Skipped: {whatsappResult.skipped}</span>
              <span style={{ color: '#ef4444', fontWeight: 700 }}>✗ Failed: {whatsappResult.failed}</span>
            </div>

            <div style={{ padding: '8px 22px 16px', overflowY: 'auto' }}>
              {(whatsappResult.details || []).length === 0 ? (
                <p style={{ color: '#6b7280' }}>No recipients processed.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#374151' }}>
                      <th style={{ padding: '8px 6px', borderBottom: '2px solid #eee' }}>Tutee</th>
                      <th style={{ padding: '8px 6px', borderBottom: '2px solid #eee' }}>Status</th>
                      <th style={{ padding: '8px 6px', borderBottom: '2px solid #eee' }}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {whatsappResult.details.map((d, i) => {
                      const color = d.status === 'sent' ? '#10b981' : d.status === 'failed' ? '#ef4444' : '#f59e0b';
                      return (
                        <tr key={i}>
                          <td style={{ padding: '8px 6px', borderBottom: '1px solid #f3f3f3' }}>{d.tutee || '—'}</td>
                          <td style={{ padding: '8px 6px', borderBottom: '1px solid #f3f3f3', color, fontWeight: 600, textTransform: 'capitalize' }}>{d.status}</td>
                          <td style={{ padding: '8px 6px', borderBottom: '1px solid #f3f3f3', color: '#6b7280' }}>{d.reason || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ padding: '14px 22px', borderTop: '1px solid #eee', textAlign: 'right' }}>
              <button
                onClick={() => setWhatsappResult(null)}
                style={{ padding: '10px 22px', background: '#893C3B', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default FinanceICInvoiceManagement;
